import { registerDecorator } from 'class-validator';
/**
 * How far past `startDate` an exercise programme's `endDate` may reach.
 *
 * The programme calendar is materialised **one row per day** per programme
 * (`CalculateExerciseProgramCalendarQuery` walks the range day by day), so the
 * range length is a direct multiplier on synchronous work and on rows written.
 * Left unbounded it is a resource-exhaustion vector: `@IsDateString()` happily
 * accepts `"9999-12-31"`, and on 2026-07-29 a long-dated programme wedged the
 * dev backend's event loop for >52s until Swarm SIGKILLed it (exit 137).
 *
 * Five years is far beyond any real course of treatment while keeping the
 * worst-case rebuild ~1,826 days per programme.
 *
 * Mirrors the guard `RruleExpansionService` already applies to appointment
 * series (`UnboundedRruleError`, 10,000 occurrences) — same bug class, same
 * shape of defence.
 */
export const EXERCISE_PROGRAM_CALENDAR_HORIZON_YEARS = 5;
/** The horizon in days, for callers that clamp rather than reject. */
export const EXERCISE_PROGRAM_CALENDAR_HORIZON_DAYS = EXERCISE_PROGRAM_CALENDAR_HORIZON_YEARS * 366;
/**
 * Rejects an `endDate` more than {@link EXERCISE_PROGRAM_CALENDAR_HORIZON_YEARS}
 * years past *now*.
 *
 * Deliberately measured from now rather than from the DTO's own `startDate`:
 * cross-property validation would let a far-future `startDate` carry an equally
 * far-future `endDate` through, which bounds the range length but still pushes
 * the calendar arbitrarily far out. Absent/empty values pass — pair with
 * `@IsOptional()` as the other date decorators here do.
 *
 * This is the boundary defence only. It cannot help rows already persisted with
 * a longer range, so the rebuild clamps independently; see
 * `UpdateExerciseProgramCalendarForExerciseProgramCommand`.
 */
export function IsWithinCalendarHorizon(validationOptions) {
    const message = validationOptions?.message ??
        `endDate must be within ${EXERCISE_PROGRAM_CALENDAR_HORIZON_YEARS} years from today.`;
    return function (object, propertyName) {
        registerDecorator({
            name: 'isWithinCalendarHorizon',
            target: object.constructor,
            propertyName: propertyName,
            constraints: [],
            options: { ...validationOptions, message },
            validator: {
                validate(value) {
                    if (value === null || value === undefined || value === '') {
                        return true;
                    }
                    const parsed = value instanceof Date ? value : new Date(value);
                    if (Number.isNaN(parsed.getTime())) {
                        // Not our concern — @IsDateString already reports the malformed value.
                        return true;
                    }
                    const horizon = new Date();
                    horizon.setFullYear(horizon.getFullYear() + EXERCISE_PROGRAM_CALENDAR_HORIZON_YEARS);
                    return parsed.getTime() <= horizon.getTime();
                },
            },
        });
    };
}
//# sourceMappingURL=calendar-horizon.js.map